import { extract, isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { ErrorNotification } from '../../../../../components/design/ErrorNotification/ErrorNotification';
import { useErrorNotificationAdapter } from '../../../../../components/design/ErrorNotification/hooks/use-error-notification-adapter';
import { useFormErrorNotificationContext } from '../../../../../contexts/form-error-notification-context/form-error-notification-context';
import type { FormErrorField } from '../../../../../contexts/form-error-notification-context/form-error-notification-context-types/form-error-field';
import { useCreateInvoiceContext } from '../../create-invoice-page-contexts/create-invoice-context/create-invoice-context';
import type { CreateInvoiceTab } from '../../create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/create-invoice-tab';
import type { InvoiceFormInput } from '../CreateInvoiceForm/create-invoice-form-types/invoice-form-input';
import type { NewInvoiceForm } from '../CreateInvoiceForm/create-invoice-form-types/new-invoice-form';
import { CREATE_INVOICE_ERROR_NOTIFICATION_DELAY_FOCUS } from './create-invoice-error-notification-consts';

export const CreateInvoiceErrorNotification: React.FC = () => {
  const { setCreateInvoiceTab } = useCreateInvoiceContext();
  const { errorsFields, registerErrorCustomField, showErrorNotification, setShowErrorNotification } =
    useFormErrorNotificationContext<InvoiceFormInput, CreateInvoiceTab>();
  const {
    formState: { errors },
    setFocus,
  } = useFormContext<InvoiceFormInput, NewInvoiceForm>();

  const mappedErrors = useErrorNotificationAdapter(extract(errorsFields, 'fieldPath'), errors);

  registerErrorCustomField('productsValidation');

  return (
    <ErrorNotification
      open={showErrorNotification && mappedErrors.length > 0}
      errors={mappedErrors}
      description="Find and fix the errors before saving the invoice"
      onGoTo={(fieldPath) => {
        const tab = getFieldPathTab(fieldPath as FieldPath<InvoiceFormInput>, errorsFields);
        if (isNotNullOrUndefined(tab)) {
          setCreateInvoiceTab(tab);
        }

        delayFocus(fieldPath as FieldPath<InvoiceFormInput>, setFocus);
      }}
      onClose={() => setShowErrorNotification(false)}
    />
  );
};

function getFieldPathTab(
  fieldPath: FieldPath<InvoiceFormInput>,
  errorsFields: FormErrorField<InvoiceFormInput, CreateInvoiceTab>[],
): CreateInvoiceTab | undefined {
  return errorsFields.find((errorField) => errorField.fieldPath === fieldPath)?.tab;
}

function delayFocus(
  fieldPath: FieldPath<InvoiceFormInput>,
  setFocus: (fieldPath: FieldPath<InvoiceFormInput>) => void,
) {
  setTimeout(() => {
    setFocus(fieldPath);
  }, CREATE_INVOICE_ERROR_NOTIFICATION_DELAY_FOCUS);
}
